import { Component, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { TaxTypeModel } from 'src/app/models/management/tax-type.model';
import { CountryService } from 'src/app/services/management/country.service';
import { TaxTypeService } from 'src/app/services/management/tax-type.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-tax-type',
  templateUrl: './register-tax-type.component.html',
  styleUrls: ['./register-tax-type.component.scss'],
  standalone: false
})
export class RegisterTaxTypeComponent implements OnInit {
  countryId: number = 0;
  formTaxType: FormGroup = new FormGroup({});
  listCountries: any[] = [];
  listJurisdictions: any[] = [];
  subscription: Subscription;
  taxTypeId: number = 0;
  taxTypeContract: TaxTypeModel = new TaxTypeModel();

  constructor(
    public config: DynamicDialogConfig,
    private countryService: CountryService,
    private formBuilder: FormBuilder,
    @Optional() private ref: DynamicDialogRef,
    private taxTypeService: TaxTypeService,
    private toastHandlerService: ToastHandlerService) {
    this.getAllLists();
  }

  ngOnInit(): void {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.subscription = input$?.subscribe((inputs: any) => {
      this.taxTypeContract = inputs.taxTypeModel;
      if (this.taxTypeContract && this.taxTypeContract.taxTypeId != 0) {
        this.taxTypeId = this.taxTypeContract.taxTypeId;
      }
      this.validateForm(new TaxTypeModel());
      this.handleInitialState();
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  async getAllLists() {
    try {
      const [countries, jurisdictions] = await Promise.all([
        firstValueFrom(this.countryService.getAllCountriesForSelect()),
        firstValueFrom(this.taxTypeService.getAllJurisdictionsForSelect()),
      ]);
      this.listCountries = countries;
      this.listJurisdictions = jurisdictions;
      return true;
    } catch (error) {
      console.error('Error al obtener las listas.', error);
      return false;
    }
  }

  changeTaxType() {
    this.countryId = this.formTaxType.get("countryId")?.value;
    const code = this.formTaxType.get("code")?.value;

    if (!this.countryId || this.countryId == 0) {
      this.toastHandlerService.showToast('warning', 'Seleccione el páis.');
    }

    if (code != "") {
      this.getTaxTypeByCode(this.countryId, code);
    } else {
      this.createNewTaxType();
    }
  }

  async createNewTaxType() {
    this.taxTypeId = 0;
    this.taxTypeContract = new TaxTypeModel();
    if (this.countryId != 0) {
      this.taxTypeContract.countryId = this.countryId;
    }
    await this.validateForm(this.taxTypeContract);
  }

  async getCountry(countryId: number) {
    this.countryId = countryId;
    this.createNewTaxType();
  }

  async getTaxTypeByCode(countryId: number, code: string) {
    try {
      this.taxTypeContract = await firstValueFrom(this.taxTypeService.getTaxTypeByKeys(countryId, code));
      this.taxTypeId = this.taxTypeContract.taxTypeId;
      this.validateForm(this.taxTypeContract);
    } catch (error) {
      console.error('Error al buscar el tipo impuesto por código.', error);
    }
  }

  async getTaxTypeById() {
    try {
      this.taxTypeContract = await firstValueFrom(this.taxTypeService.getTaxTypeById(this.taxTypeId));
      this.taxTypeId = this.taxTypeContract.taxTypeId;
      await this.validateForm(this.taxTypeContract);
    } catch (error) {
      console.error('Error al buscar el tipo impuesto por id.', error);
    }
  }

  async handleInitialState() {
    if (this.taxTypeId !== null && this.taxTypeId !== 0) {
      await this.getTaxTypeById();
    } else {
      this.createNewTaxType();
    }
  }

  async send() {
    Object.keys(this.formTaxType.controls).forEach(key => {
      const control = this.formTaxType.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });

    if (this.formTaxType.valid) {
      const modelTaxType = this.formTaxType.getRawValue();

      if (this.taxTypeId == 0) {
        try {
          const response = await firstValueFrom(this.taxTypeService.createTaxType(modelTaxType));
          this.toastHandlerService.showToast('success', `El tipo impuesto fue creado exitosamente.`);
          this.taxTypeContract = response;
          this.taxTypeId = this.taxTypeContract.taxTypeId;
          await this.validateForm(this.taxTypeContract);
        } catch (error) {
          console.error('Error al crear el tipo impuesto.', error);
        }
      } else {
        this.taxTypeContract.name = modelTaxType.name;
        this.taxTypeContract.jurisdictionId = modelTaxType.jurisdictionId;
        try {
          const response = await firstValueFrom(this.taxTypeService.updateTaxType(this.taxTypeContract));
          this.toastHandlerService.showToast('success', `El tipo impuesto fue modificado exitosamente.`);
          this.taxTypeContract = response;
          await this.validateForm(this.taxTypeContract);
        } catch (error) {
          console.error('Error al actualizar el tipo impuesto.', error);
        }
      }
      const output$ = this.config.data?.output$;
      output$?.next({ type: 'reload' });
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      countryId: {
        required: 'El país es obligatirio.',
        min: 'El país es obligatirio.',
      },
      code: {
        required: 'El código es obligatirio.',
        maxlength: 'El campo código permite máximo 2 caracteres.'
      },
      name: {
        required: 'El nombre es obligatorio.',
        maxlength: 'El campo nombre permite máximo 30 caracteres.'
      },
      jurisdictionId: {
        required: 'La jurisdicción es obligatirio.',
        min: 'La jurisdicción es obligatirio.',
      },
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formTaxType.get(controlName);
      if (control && control.invalid && control.errors) {
        const errors = control.errors;
        Object.keys(errors).forEach(errorKey => {
          const message = controlMessages[controlName][errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message, 'Atención');
          }
        });
      }
    });
  }

  validateForm(taxType: TaxTypeModel) {
    this.formTaxType = this.formBuilder.group({
      countryId: [taxType.countryId, [Validators.required, Validators.min(1)]],
      code: [taxType.code, [Validators.required, Validators.min(1)]],
      name: [taxType.name, [Validators.required]],
      jurisdictionId: [taxType.jurisdictionId, [Validators.required, Validators.min(1)]]
    })
  }
}
